import { io, Socket } from 'socket.io-client';

class SocketService {
  private socket: Socket | null = null;
  private serverUrl: string;

  constructor() {
    // Always use Railway backend for production
    this.serverUrl = process.env.NODE_ENV === 'production'
      ? 'https://universal-matching-game-production.up.railway.app'
      : 'http://localhost:5000';
  }

  /**
   * Connect to Socket.IO server
   */
  connect(userId: number): Socket {
    if (this.socket?.connected) {
      return this.socket;
    }

    this.socket = io(this.serverUrl, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    this.socket.on('connect', () => {
      console.log('✅ Connected to Socket.IO server');
      // Register user
      this.socket?.emit('register_user', userId);
    });

    this.socket.on('disconnect', () => {
      console.log('❌ Disconnected from Socket.IO server');
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error);
    });

    return this.socket;
  }

  /**
   * Disconnect from server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Get current socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Join a room
   */
  joinRoom(roomId: number, userId: number, username: string) {
    console.log(`📤 Attempting to emit join_room:`, { roomId, userId, username });

    if (!this.socket) {
      console.error('❌ Socket not initialized');
      return;
    }

    if (!this.socket.connected) {
      console.log('⏳ Socket not connected yet, waiting...');
      this.socket.once('connect', () => {
        console.log('✅ Socket connected, now emitting join_room');
        this.socket?.emit('join_room', { roomId, userId, username });
      });
    } else {
      console.log('✅ Socket already connected, emitting join_room');
      this.socket.emit('join_room', { roomId, userId, username });
    }
  }

  /**
   * Leave a room
   */
  leaveRoom(roomId: number, userId: number) {
    this.socket?.emit('leave_room', { roomId, userId });
  }

  /**
   * Toggle ready status
   */
  toggleReady(roomId: number, userId: number) {
    this.socket?.emit('toggle_ready', { roomId, userId });
  }

  /**
   * Start game (host only)
   */
  startGame(roomId: number, hostUserId: number) {
    this.socket?.emit('start_game', { roomId, hostUserId });
  }

  /**
   * Notify match attempt
   */
  matchAttempt(roomId: number, userId: number, correct: boolean) {
    this.socket?.emit('match_attempt', { roomId, userId, correct });
  }

  /**
   * Submit final result
   */
  playerFinished(
    roomId: number,
    userId: number,
    username: string,
    completionTime: number,
    totalAttempts: number,
    correctMatches: number,
    wrongMatches: number,
    averageSpeed: number
  ) {
    this.socket?.emit('player_finished', {
      roomId,
      userId,
      username,
      completionTime,
      totalAttempts,
      correctMatches,
      wrongMatches,
      averageSpeed
    });
  }

  /**
   * Quit game and notify other players
   */
  quitGame(roomId: number, userId: number, username: string) {
    this.socket?.emit('quit_game', { roomId, userId, username });
  }

  /**
   * Listen to events
   */
  on(event: string, callback: (...args: any[]) => void) {
    this.socket?.on(event, callback);
  }

  /**
   * Remove event listener
   */
  off(event: string, callback?: (...args: any[]) => void) {
    this.socket?.off(event, callback);
  }
}

const socketService = new SocketService();
export default socketService;
